/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LearnerShell, { ProgressBar, EmptyState } from "./learnerShell";
import { learnerPath } from "./_learnerConfig";
import LearnerCourseCard, {
  formatCourseDuration,
  isMandatoryCourse,
  normalizeCourseRow,
} from "./learnerCourseCard";
import { globalAlert } from "../actions/commonActions";
import { useAuth } from "./learnerAuth";
import {
  learnerCoursesApi,
  learnerEnrolApi,
  learnerEnrolmentsApi,
} from "./_learnerActions";

const listOf = (data) =>
  Array.isArray(data) ? data : data?.list || data?.courses || data?.enrolments || [];

const greetingForHour = (hour, t) => {
  if (hour < 12) {
    return t("learner.lms.dashboard.goodMorning", "Good morning");
  }
  if (hour < 18) {
    return t("learner.lms.dashboard.goodAfternoon", "Good afternoon");
  }
  return t("learner.lms.dashboard.goodEvening", "Good evening");
};

const relativeDateLabel = (dateValue, t, language = "en") => {
  if (!dateValue) return t("learner.lms.dashboard.recently", "Recently");
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return t("learner.lms.dashboard.recently", "Recently");
  }
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return t("learner.lms.dashboard.today", "Today");
  if (diffDays === 1) return t("learner.lms.dashboard.yesterday", "Yesterday");
  if (diffDays < 7) {
    return t("learner.lms.dashboard.daysAgo", "{{count}} days ago", {
      count: diffDays,
    });
  }
  return date.toLocaleDateString(language || "en-GB", {
    day: "2-digit",
    month: "short",
    year: now.getFullYear() === date.getFullYear() ? undefined : "numeric",
  });
};

const sumWatchedSeconds = (enrolment = {}) => {
  const sumRows = (rows = []) =>
    rows.reduce((sum, row) => sum + (Number(row?.watchedSeconds) || 0), 0);

  return (
    sumRows(enrolment?.courseIntroProgress) +
    (enrolment?.moduleProgress || []).reduce((sum, moduleRow) => {
      const sectionSeconds = (moduleRow?.sectionProgress || []).reduce(
        (sectionSum, sectionRow) => sectionSum + sumRows(sectionRow?.assetProgress),
        0
      );
      return (
        sum +
        sumRows(moduleRow?.moduleIntroProgress) +
        sumRows(moduleRow?.completionAssetProgress) +
        sumRows(moduleRow?.supportServiceAssetProgress) +
        sectionSeconds
      );
    }, 0)
  );
};

const toHoursLabel = (seconds, t) => {
  const hours = (Number(seconds) || 0) / 3600;
  if (hours >= 10) {
    return t("learner.lms.dashboard.hoursShort", "{{count}}h", {
      count: Math.round(hours),
    });
  }
  if (hours >= 1) {
    return t("learner.lms.dashboard.hoursShort", "{{count}}h", {
      count: hours.toFixed(1).replace(/\.0$/, ""),
    });
  }
  const minutes = Math.round((Number(seconds) || 0) / 60);
  return t("learner.lms.dashboard.minutesShort", "{{count}}m", {
    count: minutes || 0,
  });
};

const routeCourseId = (row = {}) => encodeURIComponent(row?.slug || row?.courseId || "");

const LearnerDashboard = () => {
  const { orgName } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { learner } = useAuth();
  const availableRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enrollingId, setEnrollingId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError("");

    const [coursesRes, enrolmentsRes] = await Promise.allSettled([
      learnerCoursesApi(),
      learnerEnrolmentsApi(),
    ]);

    const coursesPayload = coursesRes.status === "fulfilled" ? coursesRes.value?.data : null;
    const enrolmentsPayload = enrolmentsRes.status === "fulfilled" ? enrolmentsRes.value?.data : null;

    if (coursesPayload?.status !== 200) {
      setLoading(false);
      setError(
        coursesPayload?.message ||
        enrolmentsPayload?.message ||
        t("common.somethingWentWrong")
      );
      return;
    }

    const courseRows = listOf(coursesPayload?.data).map(normalizeCourseRow);
    const enrolmentRowsRaw = enrolmentsPayload?.status === 200 ? listOf(enrolmentsPayload?.data) : [];
    const enrolmentRows = enrolmentRowsRaw.map(normalizeCourseRow);

    const courseMap = new Map(courseRows.map((row) => [String(row.courseId), row]));
    const mergedEnrolments = enrolmentRows.map((row) => ({
      ...(courseMap.get(String(row.courseId)) || {}),
      ...row,
      enrolled: true,
      contentSummary:
        courseMap.get(String(row.courseId))?.contentSummary || row?.contentSummary,
    }));
    const enrolmentMap = new Map(mergedEnrolments.map((row) => [String(row.courseId), row]));

    setRows(
      courseRows.map((row) => enrolmentMap.get(String(row.courseId)) || row)
    );
    setEnrolments(enrolmentRowsRaw);
    setLoading(false);
  };

  const enrol = async (courseId, courseSlug = "") => {
    setEnrollingId(courseId);
    try {
      const res = await learnerEnrolApi(courseId);
      const { status, message } = res?.data || {};
      if (status === 200) {
        const targetCourseId = encodeURIComponent(courseSlug || courseId || "");
        if (targetCourseId) navigate(learnerPath(orgName, "course/" + targetCourseId));
      } else {
        globalAlert("warning", message);
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setEnrollingId(null);
  };

  const dashboard = useMemo(() => {
    const enrolledRows = rows.filter((row) => row.enrolled);
    const completedRows = enrolledRows.filter(
      (row) => String(row.status || "").toLowerCase() === "completed"
    );
    const inProgressRows = enrolledRows.filter((row) => {
      const status = String(row.status || "").toLowerCase();
      return status !== "completed" && (Number(row.progressPercent) || 0) > 0;
    });
    const notStartedRows = enrolledRows.filter(
      (row) => (Number(row.progressPercent) || 0) === 0
    );
    const availableRows = rows.filter((row) => !row.enrolled);
    const mandatoryRows = availableRows.filter(isMandatoryCourse).slice(0, 3);
    const availableNextRows = availableRows.slice(0, 4);

    const heroCourse = [...inProgressRows].sort((a, b) => {
      const aTime = new Date(a.lastActivity || 0).getTime();
      const bTime = new Date(b.lastActivity || 0).getTime();
      if (bTime !== aTime) return bTime - aTime;
      return (b.progressPercent || 0) - (a.progressPercent || 0);
    })[0] || notStartedRows[0] || null;

    const hoursLearnedSeconds = enrolments.reduce(
      (sum, enrolment) => sum + sumWatchedSeconds(enrolment),
      0
    );



    return {
      enrolledRows,
      completedRows,
      inProgressRows,
      availableRows,
      mandatoryRows,
      availableNextRows,
      heroCourse,
      hoursLearnedSeconds,
    };
  }, [enrolments, rows, t]);

  const name = learner?.firstName || learner?.name || "";
  const greeting = greetingForHour(new Date().getHours(), t);

  const summaryCards = [
    {
      label: t("learner.lms.dashboard.courses", "Courses"),
      value: rows.length,
      icon: "fa-book-open",
    },
    {
      label: t("learner.lms.dashboard.inProgress", "In Progress"),
      value: dashboard.inProgressRows.length,
      icon: "fa-play-circle",
    },
    {
      label: t("learner.lms.dashboard.hoursLearned", "Hours Learned"),
      value: toHoursLabel(dashboard.hoursLearnedSeconds, t),
      icon: "fa-clock",
    },
  ];

  const scrollToAvailable = () => {
    if (availableRef.current) {
      availableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (

    <LearnerShell title={t("learner.lms.nav.dashboard", "Dashboard")} wide>
      <main className="lms-dashboard-shell">
        {/* In your LearnerDashboard component, update the hero section */}
        <section
          className="lms-dashboard-hero"
          aria-label={t("learner.lms.dashboard.heroAriaLabel", "Dashboard hero section")}
        >
          <div className="lms-dashboard-hero-copy">
            <span className="lms-dashboard-hero-kicker">
              {greeting}
              {name ? ", " + name : ""}
            </span>
            <h4
              className="lms-dashboard-hero-title"
              aria-level="1"
            >
              {dashboard.heroCourse
                ? t(
                  "learner.lms.dashboard.heroContinue",
                  "Stay on top of your learning."
                )
                : t(
                  "learner.lms.dashboard.heroStart",
                  "Start your learning journey."
                )}
            </h4>
            <p className="lms-dashboard-hero-sub">
              {dashboard.heroCourse
                ? t(
                  "learner.lms.dashboard.heroContinueSub",
                  "See your progress at a glance, keep required learning moving, and jump back into your active course below."
                )
                : t(
                  "learner.lms.dashboard.heroStartSub",
                  "You have not enrolled in a course yet. Browse available courses and choose where to begin."
                )}
            </p>
            {/* Rest of the component */}
          </div>
        </section>

        {!loading && error && (
          <div className="lms-portal-error" role="alert">
            {error}{" "}
            <button className="lms-auth-linkbtn" onClick={load}>
              {t("learner.lms.retry", "Retry")}
            </button>
          </div>
        )}

        {loading ? (
          <div className="lms-portal-loading">
            <i className="fas fa-circle-notch fa-spin mr-2"></i>
            {t("common.loading", "Loading…")}
          </div>
        ) : null}

        {!loading && !error ? (
          <>
            <section className="lms-portal-section">
              <div className="lms-dashboard-section-head">
                <div>
                  <h2 className="lms-portal-h2">
                    {t("learner.lms.dashboard.continueLearning", "Continue learning")}
                  </h2>
                  <p>
                    {dashboard.heroCourse
                      ? t(
                        "learner.lms.dashboard.continueLearningSub",
                        "Your next best step, with the progress and context you need."
                      )
                      : t(
                        "learner.lms.dashboard.continueLearningEmptySub",
                        "Browse available courses to begin learning."
                      )}
                  </p>
                </div>
              </div>

              {dashboard.heroCourse ? (
                <div className="lms-dashboard-continue-card">
                  <div className="lms-dashboard-continue-thumb">
                    {dashboard.heroCourse.thumbnailUrl ? (
                      <img src={dashboard.heroCourse.thumbnailUrl} alt="" />
                    ) : (
                      <i className="fas fa-book-open" aria-hidden="true"></i>
                    )}
                  </div>
                  <div className="lms-dashboard-continue-body">
                    <div className="lms-dashboard-continue-meta">
                      {dashboard.heroCourse.category ? (
                        <span className="lms-portal-badge blue">
                          {dashboard.heroCourse.category}
                        </span>
                      ) : null}
                    </div>
                    <h4 className="fw-bold" style={{ fontWeight: 'bold' }}>{dashboard.heroCourse.title}</h4>
                    <div className="lms-dashboard-continue-details">
                      {dashboard.heroCourse.contentSummary?.modules ? (
                        <span>
                          {dashboard.heroCourse.contentSummary.modules === 1
                            ? t("learner.lms.course.moduleCountOne", "{{n}} Module", {
                              n: dashboard.heroCourse.contentSummary.modules,
                            })
                            : t("learner.lms.course.moduleCount", "{{n}} Modules", {
                              n: dashboard.heroCourse.contentSummary.modules,
                            })}
                        </span>
                      ) : null}
                      {/* {dashboard.heroCourse.contentSummary?.parts ? (
                        <span>
                          {dashboard.heroCourse.contentSummary.parts === 1
                            ? t("learner.lms.course.partCountOne", "{{n}} part", {
                              n: dashboard.heroCourse.contentSummary.parts,
                            })
                            : t("learner.lms.course.partCount", "{{n}} parts", {
                              n: dashboard.heroCourse.contentSummary.parts,
                            })}
                        </span>
                      ) : null} */}
                      {dashboard.heroCourse.contentSummary?.videoDurationSeconds ? (
                        <span>
                          {formatCourseDuration(
                            dashboard.heroCourse.contentSummary.videoDurationSeconds
                          )}
                        </span>
                      ) : null}
                    </div>
                    <div className="lms-dashboard-continue-progress">
                      <div className="lms-dashboard-continue-progresshead">
                        <span className="h6 mb-0 fw-bold" style={{ fontWeight: 'bold', color: 'inherit' }}>{t("learner.lms.course.progress", "Progress")}</span>
                        <strong className="h6 mb-0 fw-bold" style={{ fontWeight: 'bold' }}>
                          {Math.round(Number(dashboard.heroCourse.progressPercent) || 0)}%
                        </strong>
                      </div>
                      <ProgressBar percent={Number(dashboard.heroCourse.progressPercent) || 0} />
                    </div>
                    <div className="lms-dashboard-continue-footer">
                      <span>
                        {t("learner.lms.dashboard.lastActivity", "Last activity")}: {" "}
                        {relativeDateLabel(dashboard.heroCourse.lastActivity, t, i18n.language)}
                      </span>
                      <button
                        className="btn lms-btn-primary"
                        onClick={() =>
                          navigate(
                            learnerPath(orgName, "course/" + routeCourseId(dashboard.heroCourse) + "/player")
                          )
                        }
                      >
                        {t("learner.lms.course.continue", "Continue")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="lms-dashboard-empty-card">
                  <div className="lms-dashboard-empty-icon">
                    <i className="fas fa-graduation-cap" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3>{t("learner.lms.dashboard.noEnrolledTitle", "No enrolled courses")}</h3>
                    <p>
                      {t(
                        "learner.lms.dashboard.noEnrolled",
                        "Choose one of the available courses below and begin your learning journey."
                      )}
                    </p>
                  </div>
                  <button className="btn lms-btn-primary" onClick={scrollToAvailable}>
                    {t("learner.lms.dashboard.browseCourses", "Browse courses")}
                  </button>
                </div>
              )}
            </section>

            {dashboard.mandatoryRows.length ? (
              <section className="lms-portal-section">
                <div className="lms-dashboard-section-head">
                  <div>
                    <h2 className="lms-portal-h2">
                      {t("learner.lms.dashboard.mandatoryTraining", "Mandatory training")}
                    </h2>
                    <p>
                      {t(
                        "learner.lms.dashboard.mandatoryTrainingSub",
                        "Required learning picked out first so you can act quickly."
                      )}
                    </p>
                  </div>
                </div>
                <div className="lms-course-grid">
                  {dashboard.mandatoryRows.map((row) => (
                    <LearnerCourseCard
                      row={row}
                      key={row.courseId}
                      onEnrol={enrol}
                      enrolling={enrollingId === row.courseId}
                    />
                  ))}
                </div>
              </section>
            ) : null}


            <section className="lms-portal-section" ref={availableRef}>
              <div className="lms-dashboard-section-head">
                <div>
                  <h2 className="lms-portal-h2">
                    {t("learner.lms.dashboard.available", "Available courses")}
                  </h2>
                  <p>
                    {dashboard.availableRows.length
                      ? t(
                        "learner.lms.dashboard.availableSub",
                        "Browse what is available next without wading through the full catalogue."
                      )
                      : t(
                        "learner.lms.dashboard.availableSubEmpty",
                        "You are already enrolled in everything currently available to you."
                      )}
                  </p>
                </div>
              </div>
              {dashboard.availableRows.length ? (
                <div className="lms-course-grid">
                  {dashboard.availableRows.map((row) => (
                    <LearnerCourseCard
                      row={row}
                      key={row.courseId}
                      onEnrol={enrol}
                      enrolling={enrollingId === row.courseId}
                    />
                  ))}
                </div>
              ) : dashboard.enrolledRows.length ? (
                <EmptyState
                  icon="fa-check-circle"
                  text={t(
                    "learner.lms.dashboard.allEnrolled",
                    "You have already enrolled in the courses available to you right now."
                  )}
                />
              ) : (
                <EmptyState
                  icon="fa-book-open"
                  text={t(
                    "learner.lms.dashboard.noCourses",
                    "No courses are available yet. Please check back soon."
                  )}
                />
              )}
            </section>

          </>
        ) : null}
      </main>
    </LearnerShell>

  );
};

export default LearnerDashboard;
